import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Modal from '../../../components/ui/Modal';
import Input from '../../../components/ui/Input';
import { useAuth } from '../../../contexts/AuthContext';

const LoginForm = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [adminError, setAdminError] = useState('');

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      const result = await login(formData.email, formData.password);
      if (result.success) {
        const userData = result.data?.user;
        if (userData?.requires_password_reset) {
          navigate('/password-reset');
        } else {
          navigate('/role-based-dashboard');
        }
      } else {
        setErrors({
          general: result.error || 'Invalid email or password.',
        });
      }
    } catch (error) {
      setErrors({ general: 'An error occurred during login.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdminVerify = (e) => {
    e.preventDefault();
    const adminPass = process.env.REACT_APP_ADMIN_PASSWORD || 'admin';
    if (adminPassword === adminPass) {
      setIsAdminModalOpen(false);
      setAdminPassword('');
      setAdminError('');
      navigate('/register');
    } else {
      setAdminError('Incorrect admin password');
    }
  };

  return (
    <div className='w-full'>
      {/* Error Message */}
      {errors.general && (
        <div className='bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2 mb-4'>
          <Icon name='AlertCircle' size={16} className='text-red-500 mt-0.5 flex-shrink-0' />
          <p className='text-sm text-red-600'>{errors.general}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className='space-y-4'>
        {/* Email Field */}
        <div className='group'>
          <label className='block text-sm font-semibold text-slate-700 mb-1.5 transition-colors group-focus-within:text-orange-600'>
            Email Address
          </label>
          <div className='relative'>
            <input
              type='email'
              name='email'
              placeholder='Enter your email address'
              value={formData.email}
              onChange={handleInputChange}
              disabled={isLoading}
              className={`w-full px-4 py-3 pr-10 border-2 rounded-xl bg-slate-50/50 text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-orange-400 focus:shadow-lg focus:shadow-orange-100 transition-all duration-300 text-sm ${
                errors.email ? 'border-red-300 bg-red-50/30' : 'border-slate-200 hover:border-slate-300'
              }`}
            />
            <div className='absolute right-1.5 inset-y-0 my-auto w-9 h-9 flex items-center justify-center text-slate-400' style={{ transform: 'translateZ(0)' }}>
              <Icon name='Mail' size={20} />
            </div>
          </div>
          {errors.email && (
            <p className='text-xs text-red-500 mt-1'>{errors.email}</p>
          )}
        </div>

        {/* Password Field */}
        <div className='group'>
          <label className='block text-sm font-semibold text-slate-700 mb-1.5 transition-colors group-focus-within:text-orange-600'>
            Password
          </label>
          <div className='relative'>
            <input
              type={showPassword ? 'text' : 'password'}
              name='password'
              placeholder='Enter your password'
              value={formData.password}
              onChange={handleInputChange}
              disabled={isLoading}
              className={`w-full px-4 py-3 pr-10 border-2 rounded-xl bg-slate-50/50 text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-orange-400 focus:shadow-lg focus:shadow-orange-100 transition-all duration-300 text-sm ${
                errors.password ? 'border-red-300 bg-red-50/30' : 'border-slate-200 hover:border-slate-300'
              }`}
            />
            <button
              type='button'
              onClick={() => setShowPassword(!showPassword)}
              className='absolute right-1.5 inset-y-0 my-auto w-9 h-9 flex items-center justify-center rounded-full text-slate-400 hover:text-orange-500 hover:bg-orange-50 transition-colors duration-200 focus:outline-none'
              style={{ transform: 'translateZ(0)' }}
              disabled={isLoading}
              title={showPassword ? 'Hide password' : 'Show password'}
            >
              <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
            </button>
          </div>
          {errors.password && (
            <p className='text-xs text-red-500 mt-1'>{errors.password}</p>
          )}
        </div>

        {/* Remember Me & Forgot Password */}
        <div className='flex items-center justify-between'>
          <label className='flex items-center gap-2 cursor-pointer group'>
            <input
              type='checkbox'
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className='w-4 h-4 text-orange-500 border-2 border-slate-300 rounded focus:ring-orange-500 transition-all'
              disabled={isLoading}
            />
            <span className='text-sm text-slate-600 group-hover:text-slate-800 transition-colors'>Remember me</span>
          </label>
          <button
            type='button'
            onClick={() => navigate('/forgot-password')}
            className='text-sm text-orange-500 hover:text-orange-600 font-semibold transition-all hover:underline underline-offset-2'
            disabled={isLoading}
          >
            Forgot password?
          </button>
        </div>

        {/* Sign In Button */}
        <button
          type='submit'
          disabled={isLoading}
          className='w-full py-3.5 bg-gradient-to-r from-orange-500 via-orange-500 to-amber-500 hover:from-orange-600 hover:via-orange-600 hover:to-amber-600 text-white font-bold rounded-xl shadow-lg shadow-orange-500/30 hover:shadow-xl hover:shadow-orange-500/40 transition-all duration-300 transform hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2 relative overflow-hidden group'
        >
          {/* Shimmer Effect */}
          <div className='absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700' />
          
          {isLoading ? (
            <>
              <Icon name='Loader2' size={18} className='animate-spin' />
              <span>Signing In...</span>
            </>
          ) : (
            <span className='relative z-10'>Sign In</span>
          )}
        </button>
      </form>

      {/* Create Account Link */}
      <div className='text-center mt-5'>
        <p className='text-sm text-slate-600'>
          Don't have an account?{' '}
          <button
            type='button'
            onClick={() => setIsAdminModalOpen(true)}
            className='text-orange-500 hover:text-orange-600 font-bold transition-all hover:underline underline-offset-2'
            disabled={isLoading}
          >
            Create account
          </button>
        </p>
      </div>

      {/* Admin Modal */}
      <Modal
        isOpen={isAdminModalOpen}
        onClose={() => { setIsAdminModalOpen(false); setAdminPassword(''); setAdminError(''); }}
        title="Admin Verification"
        size="sm"
      >
        <form onSubmit={handleAdminVerify} className="space-y-4">
          <p className="text-sm text-slate-500">Enter the admin password to create an account.</p>
          <Input
            label="Admin Password"
            type="password"
            placeholder="Enter admin password"
            value={adminPassword}
            onChange={(e) => { setAdminPassword(e.target.value); setAdminError(''); }}
            error={adminError}
            autoFocus
          />
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={() => { setIsAdminModalOpen(false); setAdminPassword(''); setAdminError(''); }}>
              Cancel
            </Button>
            <Button type="submit" variant="default">Verify</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default LoginForm;
